import { useTranslation } from 'react-i18next'

import {
  FaInboxRegular,
  FaMagnifyingGlassRegular,
} from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'

type Props = {
  /**
   * 一覧の代わりに出す状態。
   * - `memberUnregistered`: 組織の JEPX 会員 ID が未設定 (AC-JST-004。 応答の `jepx_member_registered = false`)
   * - `empty`: 期間指定なしで 0 件 (AC-JST-003)
   * - `emptyInPeriod`: 期間指定ありで 0 件 (AC-JST-003。 「期間を変えてお試しください」を添える)
   */
  kind: 'memberUnregistered' | 'empty' | 'emptyInPeriod'
}

/**
 * 清算書の一覧が出せない (出す対象が無い) 時の空状態パネル。
 *
 * 理由が複数あり、 理由ごとに次の行動が変わる (会員 ID は運営の設定待ち / 期間を変える) ので、
 * 表の空行ではなく `EmptyPanel` を使う (`design/ui-feasibility/components/empty-panel.md`、
 * 前例 `pages/WorkflowCases/components/ListEmptyPanel.tsx`)。 色は状態で変えず、 絞り込みの
 * 結果が 0 件の時だけ glyph を虫眼鏡にする。
 *
 * 会員 ID 未設定の見出し・説明は AC-IJM-003 (`admin/integration/jepx-member-info.md`) と
 * 同じ見出し (spec AC-JST-004)。
 */
export const SettlementStatePanel = ({ kind }: Props): React.ReactElement => {
  const { t } = useTranslation()

  switch (kind) {
    case 'memberUnregistered':
      return (
        <EmptyPanel
          description={t('page:jepxSettlement.memberUnregistered.description')}
          icon={
            <FaInboxRegular
              boxSize={EMPTY_PANEL_ICON_SIZE}
              aria-hidden="true"
            />
          }
          title={t('page:jepxSettlement.memberUnregistered.title')}
        />
      )
    case 'empty':
      return (
        <EmptyPanel
          description=""
          icon={
            <FaInboxRegular
              boxSize={EMPTY_PANEL_ICON_SIZE}
              aria-hidden="true"
            />
          }
          title={t('page:jepxSettlement.empty.title')}
        />
      )
    case 'emptyInPeriod':
      return (
        <EmptyPanel
          description={t('page:jepxSettlement.empty.periodHint')}
          icon={
            <FaMagnifyingGlassRegular
              boxSize={EMPTY_PANEL_ICON_SIZE}
              aria-hidden="true"
            />
          }
          title={t('page:jepxSettlement.empty.title')}
        />
      )
  }
}
